import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { leadsApi } from '../../api/leads.js';
import { areasApi } from '../../api/areas.js';
import { citiesApi } from '../../api/cities.js';
import { usersApi } from '../../api/users.js';
import { crmApiInstance } from '../../api/client.js';
import RowsPerPageSelect from '../../components/RowsPerPageSelect.jsx';
import ColumnFilterHeader from '../../components/ColumnFilterHeader.jsx';
import { Download, Copy, Check, ChevronDown, Search, X, Loader2, Plus, Trash2, Star, MapPin, CheckCircle2, AlertCircle } from 'lucide-react';

// ── Helpers ────────────────────────────────────────────────────────────────────

function calcAgingDays(lead) {
  const dateStr =
    lead.last_dispatched_at ||
    lead.last_delivered_at  ||
    lead.last_ordered_at    ||
    null;
  if (!dateStr) return null;
  const diffMs = Date.now() - new Date(dateStr).getTime();
  return Math.max(0, Math.floor(diffMs / 86400000));
}

function fmtDate(dateStr) {
  if (!dateStr) return 'Never';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return 'Never';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtMoney(val) {
  const n = parseFloat(val || 0);
  return `₹${n.toLocaleString('en-IN')}`;
}

function mapLead(lead) {
  const salesRep = lead.SalesRep || lead.salesRep || null;
  const roleName  = salesRep?.role?.name || '';
  const isTL      = /tl|teamlead|team_lead|sales_tl/i.test(roleName);

  const areaObj    = lead.area || lead.Area || null;
  const parentName = areaObj?.parent?.name || areaObj?.name || '';

  // Collect additional phones
  const extraPhones = [];
  if (lead.phone_2) extraPhones.push(lead.phone_2);
  if (lead.phone_3) extraPhones.push(lead.phone_3);
  if (Array.isArray(lead.phones)) {
    lead.phones.forEach(p => {
      const num = typeof p === 'string' ? p : p?.number || p?.phone || '';
      if (num && num !== lead.phone && !extraPhones.includes(num)) extraPhones.push(num);
    });
  }

  const lastOrderDateStr =
    lead.last_dispatched_at ||
    lead.last_delivered_at  ||
    lead.last_ordered_at    ||
    null;

  return {
    id:             lead.id,
    name:           lead.name || '',
    phone:          lead.phone || '',
    extraPhones,
    salesRepName:   salesRep ? `${salesRep.name}${isTL ? ' (TL)' : ''}` : '',
    salesRepId:     salesRep?.id || null,
    aging:          calcAgingDays(lead),
    totalPaid:      parseFloat(lead.total_paid   || lead.totalPaid   || 0),
    creditDue:      parseFloat(lead.credit_due   || lead.creditDue   || 0),
    lastOrderDate:  lastOrderDateStr,
    // legacy fields for CustomerModal
    owner:          lead.email || '',
    zone:           parentName || lead.source || '',
    area_id:        lead.area_id || '',
    city_id:        lead.city_id || '',
    assigned:       lead.Assignee?.name || '',
    crmPersonId:    lead.Assignee?.id ? String(lead.Assignee.id) : '',
    status:         lead.is_active === false ? 'Inactive' : 'Active',
    _raw:           lead,
  };
}

// ── AgingBadge ─────────────────────────────────────────────────────────────────
function AgingBadge({ days }) {
  if (days === null || days === undefined) {
    return (
      <span style={{ background: '#fee2e2', color: '#dc2626', borderRadius: 20, padding: '3px 11px', fontSize: 12, fontWeight: 700, display: 'inline-block' }}>
        N/A
      </span>
    );
  }
  const isOld   = days >= 90;
  const isMid   = days >= 30;
  const bg      = isOld ? '#fee2e2' : isMid ? '#fef9c3' : '#dcfce7';
  const color   = isOld ? '#dc2626' : isMid ? '#a16207' : '#15803d';
  return (
    <span style={{ background: bg, color, borderRadius: 20, padding: '3px 11px', fontSize: 12, fontWeight: 700, display: 'inline-block' }}>
      {days}
    </span>
  );
}

// ── PhoneCell ──────────────────────────────────────────────────────────────────
function PhoneCell({ phone, extraPhones }) {
  const [copied, setCopied]   = useState(false);
  const [expanded, setExpanded] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!expanded) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setExpanded(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [expanded]);

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(phone).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!phone) return <span style={{ color: 'var(--text-muted)' }}>—</span>;

  return (
    <div ref={ref} style={{ display: 'flex', alignItems: 'center', gap: 5, position: 'relative', flexWrap: 'nowrap' }}>
      <span style={{ fontSize: 12.5, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{phone}</span>
      <button
        title="Copy phone"
        onClick={handleCopy}
        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: copied ? '#16a34a' : 'var(--text-muted)', flexShrink: 0, display: 'inline-flex', alignItems: 'center' }}
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
      </button>
      {extraPhones.length > 0 && (
        <>
          <button
            title="Show more phones"
            onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
            style={{ background: '#f1f5f9', border: '1px solid var(--border)', borderRadius: 5, cursor: 'pointer', padding: '1px 6px', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0 }}
          >
            +{extraPhones.length} <ChevronDown size={10} />
          </button>
          {expanded && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, zIndex: 200,
              background: 'var(--card)', border: '1px solid var(--border)',
              borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.14)',
              padding: '6px 0', minWidth: 160, marginTop: 4,
            }}>
              {extraPhones.map((p, i) => (
                <div key={i} style={{ padding: '6px 14px', fontSize: 12.5, fontFamily: 'monospace', whiteSpace: 'nowrap', color: 'var(--text-main)' }}>
                  {p}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── AsyncSearchSelect (for CustomerModal) ─────────────────────────────────────
function AsyncSearchSelect({ value, onChange, placeholder, disabled, loadOptions, initialLabel }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery]   = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selLabel, setSelLabel] = useState(initialLabel || '');
  const wrapRef = useRef(null);
  const debRef  = useRef(null);

  useEffect(() => { if (initialLabel) setSelLabel(initialLabel); }, [initialLabel]);
  useEffect(() => { if (!value) setSelLabel(''); }, [value]);

  useEffect(() => {
    const h = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const list = await loadOptions(query);
        setOptions(list);
        if (value) {
          const found = list.find(o => o.value?.toString() === value?.toString());
          if (found) setSelLabel(found.label);
        }
      } catch (_) { setOptions([]); }
      finally { setLoading(false); }
    }, query ? 300 : 0);
    return () => clearTimeout(debRef.current);
  }, [query, isOpen]);

  const displayLabel = options.find(o => o.value?.toString() === value?.toString())?.label || selLabel;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div
        className="form-control"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: disabled ? 'not-allowed' : 'pointer', background: disabled ? '#f9fafb' : '', userSelect: 'none' }}
        onClick={() => !disabled && setIsOpen(o => !o)}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: displayLabel ? 'inherit' : '#9ca3af', fontSize: 13 }}>
          {displayLabel || placeholder}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {value && !disabled && (
            <span style={{ color: '#9ca3af', fontSize: 11, cursor: 'pointer' }}
              onClick={e => { e.stopPropagation(); onChange(''); setSelLabel(''); setQuery(''); }}>✕</span>
          )}
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{ color: '#9ca3af' }}>
            <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>
      {isOpen && !disabled && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, marginTop: 4, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
          <div style={{ padding: '8px 8px 6px', borderBottom: '1px solid #f1f5f9' }}>
            <input autoFocus type="text" value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Type to search…"
              style={{ width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 6, outline: 'none', boxSizing: 'border-box' }}
              onClick={e => e.stopPropagation()} />
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto', padding: '4px 0' }}>
            {loading ? (
              <div style={{ padding: '10px 14px', color: '#94a3b8', fontSize: 13 }}>Loading…</div>
            ) : options.length === 0 ? (
              <div style={{ padding: '10px 14px', color: '#94a3b8', fontSize: 13 }}>No results found</div>
            ) : options.map(opt => (
              <div key={opt.value}
                onClick={() => { onChange(opt.value); setSelLabel(opt.label); setIsOpen(false); setQuery(''); }}
                style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 13, background: opt.value?.toString() === value?.toString() ? '#eff6ff' : 'transparent', color: opt.value?.toString() === value?.toString() ? '#1d4ed8' : 'inherit' }}
                onMouseEnter={e => { if (opt.value?.toString() !== value?.toString()) e.currentTarget.style.background = '#f8fafc'; }}
                onMouseLeave={e => { e.currentTarget.style.background = opt.value?.toString() === value?.toString() ? '#eff6ff' : 'transparent'; }}
              >
                {opt.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── AddLocationModal — adds a delivery location to an existing lead ────────────
function AddLocationModal({ leadId, onClose, onSaved }) {
  const [loc, setLoc] = useState({ address_line_1: '', address_line_2: '', city: '', state: '', pincode: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const setL = (k, v) => setLoc(l => ({ ...l, [k]: v }));

  const handleSave = async () => {
    if (!loc.address_line_1.trim() || !loc.city.trim()) {
      setErr('Address Line 1 and City are required.');
      return;
    }
    setSaving(true); setErr('');
    try {
      const res = await leadsApi.createLocation(leadId, {
        address_line_1: loc.address_line_1.trim(),
        address_line_2: loc.address_line_2.trim() || null,
        city: loc.city.trim(),
        state: loc.state.trim() || null,
        pincode: loc.pincode.trim() || null,
        is_primary: false,
      });
      onSaved(res.data ?? res);
    } catch (e) {
      setErr(e.message || 'Failed to add location.');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1300 }}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 480, maxWidth: '92vw' }}>
        <div className="modal-header">
          <span style={{ fontWeight: 700 }}>Add Delivery Location</span>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {err && <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13 }}>{err}</div>}
          <div className="grid-2" style={{ gap: 12 }}>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}><label className="form-label">Address Line 1 *</label><input className="form-control" value={loc.address_line_1} onChange={e => setL('address_line_1', e.target.value)} placeholder="Street / building" /></div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}><label className="form-label">Address Line 2</label><input className="form-control" value={loc.address_line_2} onChange={e => setL('address_line_2', e.target.value)} placeholder="Area / landmark" /></div>
            <div className="form-group"><label className="form-label">City *</label><input className="form-control" value={loc.city} onChange={e => setL('city', e.target.value)} placeholder="City" /></div>
            <div className="form-group"><label className="form-label">State</label><input className="form-control" value={loc.state} onChange={e => setL('state', e.target.value)} placeholder="State" /></div>
            <div className="form-group"><label className="form-label">Pincode</label><input className="form-control" value={loc.pincode} onChange={e => setL('pincode', e.target.value)} placeholder="PIN code" maxLength={6} /></div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Location'}</button>
        </div>
      </div>
    </div>
  );
}

// ── CustomerModal ──────────────────────────────────────────────────────────────
function CustomerModal({ customer, onClose, onSave }) {
  const [form, setForm] = useState(
    customer
      ? {
          name: customer.name, email: customer.owner || '', phone: customer.phone,
          parentAreaId: '', area_id: customer.area_id ? String(customer.area_id) : '',
          cityId: customer.city_id ? String(customer.city_id) : '',
          shopName: customer._raw?.shop_name || customer.name || '', gst: customer._raw?.gst_number || '',
          type: customer._raw?.customer_type || 'Retail', category: customer._raw?.customer_category || 'general', notes: customer._raw?.notes || '',
          address: customer._raw?.address || customer._raw?.address_line_1 || '',
          orgId: customer._raw?.organisation_id ? String(customer._raw.organisation_id) : '',
          status: customer.status,
          salesPersonId: customer.salesRepId ? String(customer.salesRepId) : '',
          crmPersonId: customer.crmPersonId || '',
        }
      : {
          name: '', email: '', phone: '', parentAreaId: '', area_id: '', cityId: '', shopName: '', gst: '', type: 'Retail', category: 'general', notes: '', address: '', orgId: '', status: 'Active', salesPersonId: '', crmPersonId: '',
          locLine1: '', locLine2: '', locCity: '', locState: '', locPincode: '',
        }
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');

  // Additional (non-primary) contact numbers — same for create and edit; for edit,
  // initialized from the lead's existing contacts (getLeads already includes them).
  const [extraContacts, setExtraContacts] = useState(
    (customer?._raw?.contacts || []).filter(c => !c.is_primary).map(c => ({ phone: c.phone, error: '' }))
  );
  const addContact = () => setExtraContacts(c => [...c, { phone: '', error: '' }]);
  const removeContact = (i) => setExtraContacts(c => c.filter((_, idx) => idx !== i));
  const setContactPhone = (i, val) => setExtraContacts(c => c.map((item, idx) => idx === i ? { phone: val, error: '' } : item));

  // Delivery locations — only meaningful once the lead exists, so this list/add-button
  // only applies when editing; new customers capture one inline location on create instead.
  const [locations, setLocations] = useState([]);
  const [showAddLoc, setShowAddLoc] = useState(false);
  useEffect(() => {
    if (!customer?.id) return;
    leadsApi.getLocations(customer.id)
      .then(res => {
        const d = res.data ?? res;
        setLocations(Array.isArray(d) ? d : d?.locations ?? d?.data ?? []);
      })
      .catch(() => {});
  }, [customer?.id]);

  useEffect(() => {
    if (!customer?.area_id) return;
    areasApi.getById(customer.area_id)
      .then(res => {
        const area = res.data ?? res;
        setForm(f => area.parent_id
          ? { ...f, parentAreaId: String(area.parent_id) }
          : { ...f, parentAreaId: String(area.id), area_id: '' });
      })
      .catch(() => {});
  }, []);

  // Cities master list — defaults selection to Kolkata (as of now, all leads belong to Kolkata)
  const [cities, setCities] = useState([]);
  useEffect(() => {
    citiesApi.getAll().then(res => {
      const d = res.data ?? res;
      const list = Array.isArray(d) ? d : d?.data ?? [];
      setCities(list);
      const kolkata = list.find(c => c.name === 'Kolkata');
      if (kolkata) setForm(f => (f.cityId ? f : { ...f, cityId: String(kolkata.id) }));
    }).catch(() => {});
  }, []);

  const hc = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const prevParent = useRef(form.parentAreaId);
  useEffect(() => {
    if (prevParent.current !== form.parentAreaId) {
      prevParent.current = form.parentAreaId;
      setForm(f => ({ ...f, area_id: '', salesPersonId: '' }));
    }
  }, [form.parentAreaId]);

  const prevArea = useRef(form.area_id);
  useEffect(() => {
    if (prevArea.current !== form.area_id) {
      prevArea.current = form.area_id;
      setForm(f => ({ ...f, salesPersonId: '' }));
    }
  }, [form.area_id]);

  const loadOrgs        = async (q) => { const r = await crmApiInstance.get('/leads/organisations', { params: { limit: 20, sortBy: 'name', sortOrder: 'ASC', ...(q ? { search: q } : {}) } }); return (r.data?.data || r.data?.rows || []).map(o => ({ value: String(o.id), label: o.name })); };
  const loadParentAreas = async (q) => { const r = await areasApi.getAll({ parent_id: 'null', limit: 20, ...(q ? { search: q } : {}) }); const d = r.data ?? r; return (Array.isArray(d) ? d : d.data ?? []).map(a => ({ value: String(a.id), label: a.name })); };
  const loadAreas       = async (q) => { if (!form.parentAreaId) return []; const r = await areasApi.getAll({ parent_id: form.parentAreaId, limit: 20, ...(q ? { search: q } : {}) }); const d = r.data ?? r; return (Array.isArray(d) ? d : d.data ?? []).map(a => ({ value: String(a.id), label: a.name })); };
  const loadStaffs      = async (q) => { const ea = form.area_id || form.parentAreaId; if (!ea) return []; const r = await usersApi.getAll({ area_id: ea, limit: 20, ...(q ? { search: q } : {}) }); const d = r.data ?? r; return (Array.isArray(d) ? d : d.data ?? []).map(s => ({ value: String(s.id), label: s.name })); };
  const loadCrmUsers    = async (q) => { const r = await crmApiInstance.get('/admin/crm-users', { params: q ? { search: q } : {} }); const d = r.data?.data || r.data || []; return (Array.isArray(d) ? d : []).map(u => ({ value: String(u.id), label: u.name })); };

  const handleSave = async () => {
    if (!form.name?.trim())     { setErr('Full Name is required.'); return; }
    if (form.email && !form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) { setErr('Valid email required.'); return; }
    if (!form.phone?.match(/^[6-9]\d{9}$/)) { setErr('Valid 10-digit phone required.'); return; }
    if (!form.shopName?.trim()) { setErr('Shop / Company Name is required.'); return; }
    if (!form.address?.trim())  { setErr('Address is required.'); return; }
    if (form.gst && !form.gst.match(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/)) { setErr('Invalid GST format.'); return; }

    let contactsOk = true;
    const checkedContacts = extraContacts.map(c => {
      const val = c.phone.trim();
      if (val && !/^[6-9]\d{9}$/.test(val)) { contactsOk = false; return { ...c, error: 'Valid 10-digit number required.' }; }
      return { ...c, error: '' };
    });
    if (!contactsOk) { setExtraContacts(checkedContacts); return; }

    setSaving(true); setErr('');
    try {
      const validContacts = extraContacts.map(c => ({ phone: c.phone.trim() })).filter(c => c.phone);
      await onSave(form, validContacts);
    }
    catch (e) { setErr(e.message || 'Save failed.'); setSaving(false); }
  };

  const SECTION = { marginBottom: 10, fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ width: '920px', maxWidth: '95vw', maxHeight: '92vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <span style={{ fontWeight: 700 }}>{customer ? 'Edit Customer' : 'Add Customer'}</span>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {err && <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13 }}>{err}</div>}
          <div style={SECTION}>Personal Information</div>
          <div className="grid-2" style={{ marginBottom: 20 }}>
            <div className="form-group"><label className="form-label">Full Name *</label><input className="form-control" value={form.name} onChange={e => hc('name', e.target.value)} placeholder="Enter full name" /></div>
            <div className="form-group"><label className="form-label">Email Address</label><input className="form-control" value={form.email} onChange={e => hc('email', e.target.value)} placeholder="email@example.com" /></div>
            <div className="form-group"><label className="form-label">Phone Number *</label><input className="form-control" value={form.phone} onChange={e => hc('phone', e.target.value)} placeholder="10-digit mobile number" maxLength={10} /></div>
            <div className="form-group"><label className="form-label">Customer Type</label><select className="form-control" value={form.type} onChange={e => hc('type', e.target.value)}>{['Retail', 'Wholesale', 'Distributor', 'Corporate'].map(t => <option key={t}>{t}</option>)}</select></div>
            <div className="form-group"><label className="form-label">Customer Category</label><select className="form-control" value={form.category} onChange={e => hc('category', e.target.value)}>{[['general', 'General Customer'], ['ecommerce', 'Ecommerce'], ['tender', 'Tender']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
          </div>
          <div style={SECTION}>Business Information</div>
          <div className="grid-2" style={{ marginBottom: 20 }}>
            <div className="form-group"><label className="form-label">Shop / Company Name *</label><input className="form-control" value={form.shopName} onChange={e => hc('shopName', e.target.value)} placeholder="Enter shop or company name" /></div>
            <div className="form-group"><label className="form-label">GST Number</label><input className="form-control" value={form.gst} onChange={e => hc('gst', e.target.value.toUpperCase())} placeholder="29ABCDE1234F1Z5" maxLength={15} /></div>
            <div className="form-group"><label className="form-label">Organisation</label><AsyncSearchSelect value={form.orgId} onChange={v => hc('orgId', v)} placeholder="— Search organisation —" loadOptions={loadOrgs} /></div>
            <div className="form-group"><label className="form-label">City</label><select className="form-control" value={form.cityId} onChange={e => hc('cityId', e.target.value)}><option value="">— Select city —</option>{cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
            <div className="form-group"><label className="form-label">Region / Parent Area</label><AsyncSearchSelect value={form.parentAreaId} onChange={v => hc('parentAreaId', v)} placeholder="— Search region —" loadOptions={loadParentAreas} /></div>
            <div className="form-group"><label className="form-label">Area</label><AsyncSearchSelect key={`area-${form.parentAreaId}`} value={form.area_id} onChange={v => hc('area_id', v)} placeholder={form.parentAreaId ? '— Search area —' : '— Select Region first —'} disabled={!form.parentAreaId} loadOptions={loadAreas} /></div>
            <div className="form-group"><label className="form-label">Sales Person</label><AsyncSearchSelect key={`staff-${form.area_id || form.parentAreaId}`} value={form.salesPersonId} onChange={v => hc('salesPersonId', v)} placeholder="— Search sales person —" loadOptions={loadStaffs} /></div>
            <div className="form-group"><label className="form-label">CRM Person</label><AsyncSearchSelect value={form.crmPersonId} onChange={v => hc('crmPersonId', v)} placeholder="— Search CRM user —" initialLabel={customer?.assigned || ''} loadOptions={loadCrmUsers} /></div>
          </div>
          <div style={SECTION}>Address &amp; Notes</div>
          <div style={{ display: 'grid', gap: 16, marginBottom: 20 }}>
            <div className="form-group"><label className="form-label">Full Address *</label><textarea className="form-control" value={form.address} onChange={e => hc('address', e.target.value)} placeholder="Enter complete address with city and PIN code" rows={2} /></div>
            <div className="form-group"><label className="form-label">Notes</label><textarea className="form-control" value={form.notes} onChange={e => hc('notes', e.target.value)} placeholder="Any special instructions or notes…" rows={2} /></div>
            <div className="form-group" style={{ maxWidth: 200 }}><label className="form-label">Status</label><select className="form-control" value={form.status} onChange={e => hc('status', e.target.value)}><option>Active</option><option>Inactive</option></select></div>
          </div>

          <div style={SECTION}>Additional Contact Numbers</div>
          <div style={{ marginBottom: 20 }}>
            {extraContacts.map((c, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginBottom: 6 }}>
                <div style={{ flex: 1 }}>
                  <input className="form-control" value={c.phone} onChange={e => setContactPhone(i, e.target.value)} placeholder={`Contact ${i + 1} — 10-digit number`} maxLength={10} />
                  {c.error && <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#dc2626', marginTop: 4 }}><AlertCircle size={11} /> {c.error}</span>}
                </div>
                <button type="button" title="Set as primary" onClick={() => { const newPrimary = c.phone.trim(); const oldPrimary = form.phone.trim(); hc('phone', newPrimary); setExtraContacts(cs => cs.map((item, idx) => idx === i ? { phone: oldPrimary, error: '' } : item)); }}
                  disabled={!c.phone.trim() || !/^[6-9]\d{9}$/.test(c.phone.trim())}
                  style={{ marginTop: 2, background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', color: '#3b82f6', padding: '5px 8px', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  <Star size={12} /> Primary
                </button>
                <button type="button" title="Remove" onClick={() => removeContact(i)} style={{ marginTop: 2, background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', padding: 5 }}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
            <button type="button" onClick={addContact} className="btn btn-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '5px 10px' }}>
              <Plus size={12} /> Add Number
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ ...SECTION, marginBottom: 0 }}>Delivery Locations</div>
            {customer && (
              <button type="button" onClick={() => setShowAddLoc(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', fontSize: 12.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                + Add New Location
              </button>
            )}
          </div>
          {customer ? (
            <div style={{ marginBottom: 4 }}>
              {locations.length === 0 ? (
                <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>No delivery locations saved yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {locations.map(loc => (
                    <div key={loc.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: 13 }}>
                      <MapPin size={12} style={{ color: '#3b82f6', flexShrink: 0 }} />
                      <span style={{ flex: 1 }}>{[loc.address_line_1, loc.address_line_2, loc.city, loc.state, loc.pincode].filter(Boolean).join(', ')}</span>
                      {loc.is_primary && <span style={{ fontSize: 11, fontWeight: 700, color: '#3b82f6', background: 'rgba(59,130,246,0.08)', borderRadius: 4, padding: '2px 6px' }}>Primary</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="grid-2" style={{ gap: 12, marginBottom: 4 }}>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}><label className="form-label">Address Line 1</label><input className="form-control" value={form.locLine1} onChange={e => hc('locLine1', e.target.value)} placeholder="Street / building" /></div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}><label className="form-label">Address Line 2</label><input className="form-control" value={form.locLine2} onChange={e => hc('locLine2', e.target.value)} placeholder="Area / landmark" /></div>
              <div className="form-group"><label className="form-label">City</label><input className="form-control" value={form.locCity} onChange={e => hc('locCity', e.target.value)} placeholder="City" /></div>
              <div className="form-group"><label className="form-label">State</label><input className="form-control" value={form.locState} onChange={e => hc('locState', e.target.value)} placeholder="State" /></div>
              <div className="form-group"><label className="form-label">Pincode</label><input className="form-control" value={form.locPincode} onChange={e => hc('locPincode', e.target.value)} placeholder="PIN code" maxLength={6} /></div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Customer'}</button>
        </div>
      </div>
      {showAddLoc && customer && (
        <AddLocationModal
          leadId={customer.id}
          onClose={() => setShowAddLoc(false)}
          onSaved={(created) => { setLocations(prev => [...prev, created]); setShowAddLoc(false); }}
        />
      )}
    </div>
  );
}

// ── CSV Export ─────────────────────────────────────────────────────────────────
function exportToCSV(rows) {
  const headers = [
    'SR. NO.', 'Customer Name', 'Phone', 'Additional Phones',
    'Sales Rep', 'Aging (Days)', 'Total Paid (INR)', 'Credit Due (INR)',
    'Last Order Date', 'Account Status', 'Area / Source', 'CRM Person',
    'GST Number', 'Email', 'Address', 'Customer Type', 'Notes',
  ];

  const escape = v => `"${String(v ?? '').replace(/"/g, '""')}"`;

  const csvRows = rows.map((c, i) => [
    i + 1,
    c.name,
    c.phone,
    c.extraPhones.join('; '),
    c.salesRepName || '—',
    c.aging !== null ? c.aging : 'N/A',
    c.totalPaid,
    c.creditDue,
    c.lastOrderDate ? new Date(c.lastOrderDate).toLocaleDateString('en-IN') : 'Never',
    c.status,
    c.zone,
    c.assigned,
    c._raw?.gst_number   || '',
    c._raw?.email        || '',
    c._raw?.address      || c._raw?.address_line_1 || '',
    c._raw?.customer_type || '',
    c._raw?.notes        || '',
  ].map(escape).join(','));

  const csv  = [headers.map(escape).join(','), ...csvRows].join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `customers_${new Date().toISOString().split('T')[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function AdminCustomersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [customers,    setCustomers]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [exporting,    setExporting]    = useState(false);
  const [search,       setSearch]       = useState('');
  const [statusFlag,   setStatusFlag]   = useState('');
  const [orderRecency, setOrderRecency] = useState('');
  const [accountStatus,setAccountStatus]= useState('');
  const [customerType, setCustomerType] = useState('');
  const [customerCategory, setCustomerCategory] = useState('');
  const [cityFilter,   setCityFilter]   = useState('');
  const [nameFilter,   setNameFilter]   = useState([]);
  const [phoneFilter,  setPhoneFilter]  = useState([]);
  const [lastOrderFilter, setLastOrderFilter] = useState([]);
  const [cities,       setCities]       = useState([]);
  const [createdFrom,  setCreatedFrom]  = useState(searchParams.get('created_from') || '');
  const [createdTo,    setCreatedTo]    = useState(searchParams.get('created_to') || '');
  const [statusCounts, setStatusCounts] = useState({});
  const [showModal,    setShowModal]    = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [page,         setPage]         = useState(1);
  const [totalCount,   setTotalCount]   = useState(0);
  const [perPage,      setPerPage]      = useState(20);

  const hasActiveFilters = !!(search || statusFlag || orderRecency || accountStatus || customerType || customerCategory || cityFilter || createdFrom || createdTo || nameFilter.length || phoneFilter.length || lastOrderFilter.length);

  const handleClearFilters = () => {
    setSearch(''); setStatusFlag(''); setOrderRecency(''); setAccountStatus(''); setCustomerType(''); setCustomerCategory(''); setCityFilter(''); setPage(1);
    setCreatedFrom(''); setCreatedTo(''); setNameFilter([]); setPhoneFilter([]); setLastOrderFilter([]);
    setSearchParams(p => { const n = new URLSearchParams(p); n.delete('created_from'); n.delete('created_to'); return n; });
  };

  // Cities master list, used for the City filter dropdown
  useEffect(() => {
    citiesApi.getAll().then(res => {
      const d = res.data ?? res;
      setCities(Array.isArray(d) ? d : d?.data ?? []);
    }).catch(() => {});
  }, []);

  const clearCreatedRange = () => {
    setCreatedFrom(''); setCreatedTo(''); setPage(1);
    setSearchParams(p => { const n = new URLSearchParams(p); n.delete('created_from'); n.delete('created_to'); return n; });
  };

  // Status flag counts
  useEffect(() => {
    leadsApi.getStatusFlags()
      .then(res => {
        const arr = res.data ?? [];
        const counts = {};
        let total = 0;
        arr.forEach(r => { counts[r.status_flag] = r.count; total += r.count; });
        counts._total = total;
        setStatusCounts(counts);
      })
      .catch(() => {});
  }, []);

  // Fetch customers
  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = { limit: perPage, page, search };
        if (statusFlag)          params.status_flag = statusFlag;
        if (orderRecency)        params.aging        = orderRecency;
        if (accountStatus !== '') params.is_active   = accountStatus;
        if (customerType)        params.customer_type = customerType;
        if (customerCategory)    params.customer_category = customerCategory;
        if (cityFilter)          params.city_id      = cityFilter;
        if (createdFrom)         params.created_from = createdFrom;
        if (createdTo)           params.created_to   = createdTo;
        if (nameFilter.length)   params.name_in      = nameFilter.join(',');
        if (phoneFilter.length)  params.phone_in     = phoneFilter.join(',');
        if (lastOrderFilter.length) params.last_ordered_at_in = lastOrderFilter.map((v) => v ?? 'null').join(',');
        const res  = await leadsApi.getAll(params);
        const data = res.data ?? res;
        const leads = data.data ?? data;
        setCustomers(Array.isArray(leads) ? leads.map(mapLead) : []);
        setTotalCount(res.count ?? res.data?.count ?? leads.length);
      } catch (err) {
        console.error('Failed to fetch customers:', err);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [page, perPage, search, statusFlag, orderRecency, accountStatus, customerType, customerCategory, cityFilter, createdFrom, createdTo, nameFilter, phoneFilter, lastOrderFilter]);

  const totalPages = Math.ceil(totalCount / perPage);

  // Handlers
  const handleSave = async (form, extraContacts = []) => {
    const payload = {
      name:          form.name,
      email:         form.email   || undefined,
      phone:         form.phone   || undefined,
      shop_name:     form.shopName,
      gst_number:    form.gst     || '',
      customer_type: form.type    || 'Retail',
      customer_category: form.category || 'general',
      notes:         form.notes   || '',
      address:       form.address || '',
      area_id:       form.area_id || form.parentAreaId || undefined,
      city_id:       form.cityId || undefined,
      status_flag:   'yellow',
      is_active:     form.status === 'Active',
      ...(form.orgId        ? { organisation_id: parseInt(form.orgId, 10) }        : {}),
      ...(form.salesPersonId ? { sales_id: parseInt(form.salesPersonId, 10) }      : {}),
      ...(form.crmPersonId   ? { assigned_to: parseInt(form.crmPersonId, 10) }     : { assigned_to: null }),
      // Inline delivery location, only collected on create — editing an existing lead
      // manages locations via the separate "Delivery Locations" list/add-location flow.
      ...(!editCustomer && (form.locLine1 || form.locCity)
        ? {
            address_line_1: form.locLine1 || undefined,
            address_line_2: form.locLine2 || undefined,
            city:           form.locCity || undefined,
            state:          form.locState || undefined,
            pincode:        form.locPincode || undefined,
          }
        : {}),
    };

    let leadId;
    if (editCustomer) {
      const res     = await leadsApi.update(editCustomer.id, payload);
      const updated = mapLead(res.data ?? res);
      leadId = editCustomer.id;
      setCustomers(cs => cs.map(c => c.id === editCustomer.id ? updated : c));
    } else {
      const res     = await leadsApi.create(payload);
      const created = mapLead(res.data ?? res);
      leadId = created.id;
      setCustomers(cs => [created, ...cs]);
    }
    await leadsApi.updateContacts(leadId, extraContacts);
    setShowModal(false);
    setEditCustomer(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this customer?')) return;
    try {
      await leadsApi.remove(id);
      setCustomers(cs => cs.filter(c => c.id !== id));
    } catch (err) {
      alert(err.message || 'Delete failed.');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = { limit: 10000, page: 1, search };
      if (statusFlag)           params.status_flag = statusFlag;
      if (orderRecency)         params.aging        = orderRecency;
      if (accountStatus !== '') params.is_active    = accountStatus;
      if (customerType)         params.customer_type = customerType;
      if (customerCategory)     params.customer_category = customerCategory;
      if (cityFilter)           params.city_id      = cityFilter;
      if (createdFrom)          params.created_from = createdFrom;
      if (createdTo)            params.created_to   = createdTo;
      const res   = await leadsApi.getAll(params);
      const data  = res.data ?? res;
      const leads = data.data ?? data;
      const all   = Array.isArray(leads) ? leads.map(mapLead) : [];
      exportToCSV(all);
    } catch (err) {
      alert('Export failed: ' + (err.message || 'Unknown error'));
    } finally {
      setExporting(false);
    }
  };

  const filterStyle = {
    fontSize: 12.5, padding: '4px 8px', borderRadius: 7,
    border: '1.5px solid var(--border)', background: 'var(--card)',
    color: 'var(--text)', fontFamily: 'inherit', height: 32,
    fontWeight: 500,
  };

  return (
    <div className="page-body">
      {showModal && (
        <CustomerModal
          customer={editCustomer}
          onClose={() => { setShowModal(false); setEditCustomer(null); }}
          onSave={handleSave}
        />
      )}

      <div className="card">
        {/* Card Header */}
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <span className="card-title">Customers</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              className="btn btn-outline btn-sm"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              onClick={handleExport}
              disabled={exporting}
              title="Export all filtered customers to CSV"
            >
              {exporting
                ? <><Loader2 size={13} className="spin" /> Exporting…</>
                : <><Download size={13} /> Export CSV</>}
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => { setEditCustomer(null); setShowModal(true); }}>
              + Add Customer
            </button>
          </div>
        </div>

        <div className="card-body">
          {/* ── Filter panel ─────────────────────────────────────── */}
          {(() => {
            const activeCount = [statusFlag, orderRecency, accountStatus !== '' && accountStatus, customerType, customerCategory, cityFilter, search].filter(Boolean).length;

            const LBL = ({ children }) => (
              <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>{children}</span>
            );
            const ClearBtn = ({ show, onClick }) => show ? (
              <button onClick={onClick} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', fontSize: 14, lineHeight: 1, padding: '0 0 0 4px' }}>×</button>
            ) : null;
            const panelInput = { ...filterStyle, height: 34 };

            return (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
                {/* header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#475569', letterSpacing: '0.01em' }}>Filters</span>
                    {hasActiveFilters && <span style={{ background: '#3b82f6', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 10, padding: '1px 7px' }}>{activeCount}</span>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {(createdFrom || createdTo) && (
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(37,99,235,0.08)', color: '#2563eb', border: '1px solid rgba(37,99,235,0.25)', padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
                        Created: {createdFrom || '…'} – {createdTo || '…'}
                        <button onClick={clearCreatedRange} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>✕</button>
                      </div>
                    )}
                    {!loading && <span style={{ fontSize: 12, color: '#94a3b8' }}>{totalCount} customers</span>}
                    {hasActiveFilters && (
                      <button onClick={handleClearFilters} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', color: '#64748b', fontSize: 12, fontWeight: 500, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ fontSize: 14, lineHeight: 1 }}>×</span> Clear all
                      </button>
                    )}
                  </div>
                </div>

                {/* filter controls */}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>

                  {/* Search */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <LBL>Search</LBL>
                      <ClearBtn show={!!search} onClick={() => { setSearch(''); setPage(1); }} />
                    </div>
                    <div className="search-bar" style={{ width: 200, height: 34 }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                      </svg>
                      <input placeholder="Search…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
                    </div>
                  </div>

                  {/* Status Flag */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <LBL>Status Flag</LBL>
                      <ClearBtn show={!!statusFlag} onClick={() => { setStatusFlag(''); setPage(1); }} />
                    </div>
                    <select value={statusFlag} onChange={e => { setStatusFlag(e.target.value); setPage(1); }} style={{ ...panelInput, width: 150 }}>
                      <option value="">All Status Flags</option>
                      <option value="gold">Gold {statusCounts['gold'] != null ? `(${statusCounts['gold']})` : ''}</option>
                      <option value="green">Green {statusCounts['green'] != null ? `(${statusCounts['green']})` : ''}</option>
                      <option value="blue">Blue {statusCounts['blue'] != null ? `(${statusCounts['blue']})` : ''}</option>
                      <option value="red">Red {statusCounts['red'] != null ? `(${statusCounts['red']})` : ''}</option>
                    </select>
                  </div>

                  {/* Order Date */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <LBL>Order Date</LBL>
                      <ClearBtn show={!!orderRecency} onClick={() => { setOrderRecency(''); setPage(1); }} />
                    </div>
                    <select value={orderRecency} onChange={e => { setOrderRecency(e.target.value); setPage(1); }} style={{ ...panelInput, width: 190 }}>
                      <option value="">Any Order Date</option>
                      <option value="1">Ordered &lt; 1 month ago</option>
                      <option value="2">Ordered 1–2 months ago</option>
                      <option value="3">Ordered 2–3 months ago</option>
                      <option value="4">Inactive &gt; 3 months / Never</option>
                      <option value="never">Never Ordered</option>
                    </select>
                  </div>

                  {/* Account Status */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <LBL>Account Status</LBL>
                      <ClearBtn show={accountStatus !== ''} onClick={() => { setAccountStatus(''); setPage(1); }} />
                    </div>
                    <select value={accountStatus} onChange={e => { setAccountStatus(e.target.value); setPage(1); }} style={{ ...panelInput, width: 120 }}>
                      <option value="">All Accounts</option>
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>

                  {/* Customer Type */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <LBL>Customer Type</LBL>
                      <ClearBtn show={!!customerType} onClick={() => { setCustomerType(''); setPage(1); }} />
                    </div>
                    <select value={customerType} onChange={e => { setCustomerType(e.target.value); setPage(1); }} style={{ ...panelInput, width: 150 }}>
                      <option value="">All Customer Types</option>
                      <option value="Retail">Retail</option>
                      <option value="Wholesale">Wholesale</option>
                      <option value="Distributor">Distributor</option>
                      <option value="Corporate">Corporate</option>
                    </select>
                  </div>

                  {/* Customer Category */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <LBL>Customer Category</LBL>
                      <ClearBtn show={!!customerCategory} onClick={() => { setCustomerCategory(''); setPage(1); }} />
                    </div>
                    <select value={customerCategory} onChange={e => { setCustomerCategory(e.target.value); setPage(1); }} style={{ ...panelInput, width: 150 }}>
                      <option value="">All Categories</option>
                      <option value="general">General Customer</option>
                      <option value="ecommerce">Ecommerce</option>
                      <option value="tender">Tender</option>
                    </select>
                  </div>

                  {/* City */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <LBL>City</LBL>
                      <ClearBtn show={!!cityFilter} onClick={() => { setCityFilter(''); setPage(1); }} />
                    </div>
                    <select value={cityFilter} onChange={e => { setCityFilter(e.target.value); setPage(1); }} style={{ ...panelInput, width: 140 }}>
                      <option value="">All Cities</option>
                      {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>

                </div>
              </div>
            );
          })()}

          {!loading && totalCount > 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
              Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, totalCount)} of {totalCount} customers
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Loading customers…</div>
          ) : (
            <div className="table-wrapper" style={{ overflowX: 'auto' }}>
              <table style={{ minWidth: 900 }}>
                <thead>
                  <tr>
                    <th style={{ width: 50 }}>SR. NO.</th>
                    <th style={{ width: '22%' }}>
                      <ColumnFilterHeader label="CUSTOMER" field="name" selected={nameFilter} onApply={(vals) => { setNameFilter(vals); setPage(1); }} />
                    </th>
                    <th style={{ width: '17%' }}>
                      <ColumnFilterHeader label="PHONE" field="phone" selected={phoneFilter} onApply={(vals) => { setPhoneFilter(vals); setPage(1); }} />
                    </th>
                    <th style={{ width: 80, textAlign: 'center' }}>AGING</th>
                    <th style={{ width: 110 }}>
                      <ColumnFilterHeader label="LAST ORDER" field="last_ordered_at" selected={lastOrderFilter} onApply={(vals) => { setLastOrderFilter(vals); setPage(1); }} formatValue={(v) => (v === null ? "(empty)" : fmtDate(v))} />
                    </th>
                    <th style={{ width: 110 }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {customers.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '28px', color: 'var(--text-muted)' }}>
                        No customers found
                      </td>
                    </tr>
                  ) : customers.map((c, idx) => (
                    <tr
                      key={c.id}
                      onClick={() => navigate(`/customers/${c.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      {/* SR. NO. */}
                      <td style={{ color: 'var(--text-muted)', fontWeight: 500, fontSize: 11.5 }}>
                        {(page - 1) * perPage + idx + 1}
                      </td>

                      {/* CUSTOMER */}
                      <td>
                        <span style={{ fontWeight: 700, color: '#0f172a', fontSize: 12.5 }}>
                          {c.name.length > 28 ? c.name.slice(0, 25) + '…' : c.name}
                        </span>
                      </td>

                      {/* PHONE */}
                      <td onClick={e => e.stopPropagation()}>
                        <PhoneCell phone={c.phone} extraPhones={c.extraPhones} />
                      </td>

                      {/* AGING */}
                      <td style={{ textAlign: 'center' }}>
                        <AgingBadge days={c.aging} />
                      </td>

                      {/* LAST ORDER */}
                      <td style={{ fontSize: 12, color: c.lastOrderDate ? 'var(--text-main)' : 'var(--text-muted)' }}>
                        {fmtDate(c.lastOrderDate)}
                      </td>


                      {/* ACTIONS */}
                      <td onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 5 }}>
                          <button
                            className="btn btn-primary btn-xs"
                            onClick={() => navigate(`/place-order?lead_id=${c.id}`)}
                          >
                            Order
                          </button>
                          <button
                            className="btn btn-outline btn-xs"
                            onClick={() => { setEditCustomer(c); setShowModal(true); }}
                          >
                            Edit
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {!loading && totalCount > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 24, gap: 16 }}>
              <button
                className="btn btn-outline"
                disabled={page === 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span style={{ fontSize: 14, color: '#475569', fontWeight: 500 }}>
                Page {page} of {totalPages || 1}
              </span>
              <button
                className="btn btn-outline"
                disabled={page >= (totalPages || 1)}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
              <RowsPerPageSelect
                value={perPage}
                onChange={(n) => { setPerPage(n); setPage(1); }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
